// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AlertProps } from '@mui/material/Alert/Alert';
import { SnackbarOrigin } from '@mui/material/Snackbar';
import { ReactElement, createContext, useContext } from 'react';

export interface ToastOptions extends AlertProps {
  /**
   * The text content to display in the toast.
   */
  element: ReactElement;

  /**
   * The duration in milliseconds to show the toast before automatically closing it.
   * Default is 10000ms (10 seconds).
   */
  duration?: number;

  /**
   * The corner where the toast should appear.
   * Default is bottom-right.
   */
  anchorOrigin?: SnackbarOrigin;
}

export interface Toast extends ToastOptions {
  /**
   * A unique identifier for the toast.
   */
  id: string;

  /**
   * The absolute timestamp (in milliseconds) when the toast should automatically close.
   * Used for reliable timeout behavior regardless of window focus state.
   */
  expiresAt: number;
}

interface ToastActions {
  /**
   * Adds a new toast with the given options.
   */
  showToast: (options: ToastOptions) => void;

  /**
   * Removes the toast with the given ID.
   */
  removeToast: (id: string) => void;
}

export const ToastContext = createContext<ToastActions | undefined>(undefined);

/**
 * A React hook that provides access to the toast context.
 */
export function useToast() {
  const context = useContext(ToastContext);

  if (context === undefined) {
    const error = 'useToast must be used within a ToastContext provider';

    throw new Error(error);
  }

  return context;
}
